import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { BSBimModel } from "../../../../../../../core/dto/beem-shot/BSBimModel/BSBimModel"
import { BSBuilding } from "../../../../../../../core/dto/beem-shot/BSBuilding/BSBuilding"
import { BSBuildingResult } from "../../../../../../../core/dto/beem-shot/BSBuildingResult/BSBuildingResult"
import { BSMaterialResultDashboard } from "../../../../../../../core/dto/beem-shot/BSMaterialResult/BSMaterialResultDashboard"
import { BSMorphology } from "../../../../../../../core/dto/beem-shot/BSMorphology/BSMorphology"
import { BSVariantDashboardDto } from "../../../../../../../core/dto/beem-shot/BSVariant/BSVariantDashboardDto"
import { useBSBimModel } from "../../../../../../../core/hooks/beem-shot/useBSBimModel"
import { useBSBuilding } from "../../../../../../../core/hooks/beem-shot/useBSBuilding"
import { useBSBuildingResult } from "../../../../../../../core/hooks/beem-shot/useBSBuildingResult"
import { useBSMaterialResults } from "../../../../../../../core/hooks/beem-shot/useBSMaterialResults"
import { useBSMorphology } from "../../../../../../../core/hooks/beem-shot/useBSMorphology"

export function useDashboardVariant(): DashboardVariantHook {
  const { getAllBSBuildingResult } = useBSBuildingResult()
  const { getAllBuilding } = useBSBuilding()
  const { getBSMaterialResultListDashboard } = useBSMaterialResults()
  const { getBSMorphology } = useBSMorphology()
  const { getBSBimModelFile } = useBSBimModel()
  const { getBimModelById } = useBSBimModel()

  const [bsVariant, setBSVariant] = useState<BSVariantDashboardDto | undefined>(undefined)
  const [bsBuilding, setBSBuilding] = useState<BSBuilding | undefined>(undefined)
  const [isBuildingLoading, setIsBuildingLoading] = useState<boolean>(false)
  const [bsBuildingResult, setBSBuildingResult] = useState<BSBuildingResult | undefined>(undefined)
  const [isBuildingResultLoading, setIsBuildingResultLoading] = useState<boolean>(false)
  const [bsMaterialResultList, setBSMaterialResultList] = useState<BSMaterialResultDashboard[]>([])
  const [bsMorphology, setBSMorphology] = useState<BSMorphology | undefined>()
  const [bsBimModel, setBSBimModel] = useState<BSBimModel | undefined>(undefined)

  const [file, setFile] = useState<File | undefined>(undefined)
  const abortControllerRef = useRef<AbortController | null>(null)

  useEffect(() => {
    if (bsBuilding?.bsBimModelId) {
      getBimModelById(bsBuilding?.bsBimModelId).then((bimModelResponse) => {
        if (bimModelResponse) {
          setBSBimModel(bimModelResponse)
        } else {
          setBSBimModel(undefined)
        }
      })
    } else {
      setBSBimModel(undefined)
    }
  }, [bsBuilding?.bsBimModelId, getBimModelById])

  useEffect(() => {
    if (bsBimModel?.id) {
      getBSBimModelFile(bsBimModel?.id, abortControllerRef)
        .then(async (blob: BlobPart | undefined) => {
          if (blob) {
            const fileName = bsBimModel.fileName && bsBimModel.fileName.length > 0 ? bsBimModel.fileName : "Model.ifc"
            const newFile = new File([blob], fileName, { type: "application/ifc" })
            setFile(newFile)
          }
        })
        .catch((error: { name: string }) => {
          if (error instanceof DOMException && error.name === "AbortError") {
            console.info("Download bim model file cancelled. Bim Model Id:", bsBimModel.id)
          }
        })
    } else {
      setFile(undefined)
    }
  }, [bsBimModel?.fileName, bsBimModel?.id, getBSBimModelFile])

  useEffect(() => {
    if (!bsBuilding?.bim && bsBuilding?.id) {
      getBSMorphology(bsBuilding?.id).then((buildingMorphology: BSMorphology) => {
        setBSMorphology(buildingMorphology)
        return buildingMorphology
      })
    } else {
      setBSMorphology(undefined)
    }
  }, [bsBuilding?.bim, bsBuilding?.id, getBSMorphology])

  const selectVariant: (variant: BSVariantDashboardDto) => void = useCallback((newVariant) => {
    setBSVariant(newVariant)
  }, [])

  useEffect(() => {
    if (bsVariant?.id) {
      setIsBuildingLoading(true)
      getAllBuilding(bsVariant.id)
        .then((response) => {
          if (response && response.length > 0) {
            setBSBuilding(response[0])
          } else {
            setBSBuilding(undefined)
          }
        })
        .finally(() => {
          setIsBuildingLoading(false)
        })
    } else {
      setBSBuildingResult(undefined)
    }
  }, [bsVariant?.id, getAllBuilding])

  useEffect(() => {
    if (bsVariant?.id) {
      setIsBuildingResultLoading(true)
      getAllBSBuildingResult(bsVariant.id)
        .then((response) => {
          if (response && response.length > 0) {
            setBSBuildingResult(response[0])
          } else {
            setBSBuildingResult(undefined)
          }
        })
        .finally(() => {
          setIsBuildingResultLoading(false)
        })
    } else {
      setBSBuildingResult(undefined)
    }
  }, [getAllBSBuildingResult, bsVariant?.id])

  useEffect(() => {
    if (bsVariant?.id && bsBuilding?.id) {
      getBSMaterialResultListDashboard(bsBuilding.id).then((response) => {
        setBSMaterialResultList(response)
      })
    } else {
      setBSMaterialResultList([])
    }
  }, [bsVariant?.id, bsBuilding?.id, getBSMaterialResultListDashboard])

  return useMemo(
    () => ({
      bsVariant,
      selectVariant,
      bsBuilding,
      isBuildingLoading,
      bsBuildingResult,
      isBuildingResultLoading,
      bsMaterialResultList,
      bsMorphology,
      bimModelFile: file,
    }),
    [
      bsVariant,
      selectVariant,
      bsBuilding,
      isBuildingLoading,
      bsBuildingResult,
      isBuildingResultLoading,
      bsMaterialResultList,
      bsMorphology,
      file,
    ]
  )
}

interface DashboardVariantHook {
  bsVariant: BSVariantDashboardDto | undefined
  bsBuilding: BSBuilding | undefined
  isBuildingLoading: boolean
  bsBuildingResult: BSBuildingResult | undefined
  isBuildingResultLoading: boolean
  bsMaterialResultList: BSMaterialResultDashboard[]
  bsMorphology: BSMorphology | undefined

  selectVariant(variant: BSVariantDashboardDto): void
  bimModelFile: File | undefined
}
